// Salary amounts come back from the API as raw strings ("350000.00"), so they
// need thousand separators before they are shown anywhere in the UI.
export const formatSalaryAmount = (amount?: string | number | null): string => {
  if (amount === null || amount === undefined || amount === '') return '';

  const value = Number(amount);
  if (!Number.isFinite(value)) return String(amount);

  return value.toLocaleString();
};

// The candidate's figure is stored without a currency, so it is shown in the
// currency the employer asked for — but only when it is actually a number.
export const formatSalaryExpectation = (
  amount?: string | number | null,
  currency?: string | null,
): string => {
  const formatted = formatSalaryAmount(amount);
  if (!formatted) return '';
  if (!currency || !Number.isFinite(Number(amount))) return formatted;

  return `${currency} ${formatted}`;
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  NGN: '₦',
  USD: '$',
  GBP: '£',
  EUR: '€',
  INR: '₹',
  GHS: '₵',
  KES: 'KSh',
};

// Symbol-prefixed form ("₦500,000") for prose aimed at candidates, where the
// three-letter code reads more like a form field than a sentence. Unknown
// currencies keep the code so nothing is ever shown without its unit.
export const formatSalaryWithSymbol = (amount: number, currency?: string | null): string => {
  const code = (currency || '').toUpperCase();
  const symbol = CURRENCY_SYMBOLS[code];
  const formatted = amount.toLocaleString(undefined, { maximumFractionDigits: 2 });

  if (symbol) return `${symbol}${formatted}`;
  return code ? `${code} ${formatted}` : formatted;
};

export const formatSalaryRange = (expectation: {
  salary_range_currency?: string | null;
  min_salary_range_amount?: string | number | null;
  max_salary_range_amount?: string | number | null;
  salary_range_term?: string | null;
}): string => {
  const min = formatSalaryAmount(expectation.min_salary_range_amount);
  const max = formatSalaryAmount(expectation.max_salary_range_amount);

  if (!min && !max) return 'N/A';

  const range = min && max ? `${min} – ${max}` : min || max;

  return `${expectation.salary_range_currency || ''} ${range} ${expectation.salary_range_term || ''}`
    .replace(/\s+/g, ' ')
    .trim();
};

/* ------------------------------------------------------------------------- *
 * Candidate salary sanity check
 *
 * Candidates answer in the currency and term the employer set for the role, so
 * the employer's own range is the only reference point we need. The failure we
 * are actually catching is a wrong number of zeros ("500" for ₦500,000), not an
 * ambitious ask — a candidate quoting well above budget is making a choice, and
 * interrupting them would be noise. So a figure is only questioned when either a
 * power-of-ten correction would land it inside the band, or it is so far out that
 * no reading of it is plausible.
 *
 * The warning never discloses the employer's figures: the suggestion is always
 * the candidate's own entry rescaled, so it reveals nothing they could not have
 * worked out themselves.
 * ------------------------------------------------------------------------- */

// How far outside the band a figure may sit before it is even considered a typo.
// 3x the top of the band is a bold ask, not a missing digit.
const OUTLIER_TOLERANCE = 3;

// Past this multiple the figure is implausible on its own terms, so it is
// questioned even when no rescale would bring it into the band.
const EXTREME_TOLERANCE = 100;

export interface EmployerSalaryBand {
  min?: string | number | null;
  max?: string | number | null;
  currency?: string | null;
}

export interface SalarySanityWarning {
  direction: 'low' | 'high';
  /** The candidate's own figure, formatted for display. */
  entered: string;
  /** The rescaled figure to offer, or null when no correction is worth suggesting. */
  suggested: string | null;
}

const toPositiveAmount = (value?: string | number | null): number | null => {
  if (value === null || value === undefined || value === '') return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

// The power of ten that moves `amount` into [lo, hi], preferring whichever result
// sits closest to the middle of that window when more than one qualifies.
const scaleInto = (amount: number, lo: number, hi: number): number | null => {
  const target = Math.sqrt(lo * hi);
  let best: number | null = null;

  for (let power = -9; power <= 9; power++) {
    if (power === 0) continue;

    // Float noise compounds fast at these magnitudes, so snap back to the
    // precision a salary figure can actually carry.
    const scaled = Number((amount * 10 ** power).toPrecision(12));
    if (scaled < lo || scaled > hi) continue;

    if (best === null || Math.abs(Math.log10(scaled / target)) < Math.abs(Math.log10(best / target))) {
      best = scaled;
    }
  }

  return best === null ? null : Number(best.toFixed(2));
};

export const getSalarySanityWarning = (
  enteredAmount: string | number | null | undefined,
  band: EmployerSalaryBand,
): SalarySanityWarning | null => {
  const amount = toPositiveAmount(enteredAmount);
  if (amount === null) return null;

  // Employers always set a range, but a role missing one leaves nothing to
  // compare against — let the figure through rather than guess at a baseline.
  const bounds = [toPositiveAmount(band.min), toPositiveAmount(band.max)]
    .filter((value): value is number => value !== null);
  if (bounds.length === 0) return null;

  const min = Math.min(...bounds);
  const max = Math.max(...bounds);

  if (amount >= min / OUTLIER_TOLERANCE && amount <= max * OUTLIER_TOLERANCE) return null;

  // Land the correction squarely inside the band where possible; a band narrow
  // enough that no power of ten fits still deserves the near miss.
  const corrected = scaleInto(amount, min, max) ?? scaleInto(amount, min / 2, max * 2);
  const isExtreme = amount < min / EXTREME_TOLERANCE || amount > max * EXTREME_TOLERANCE;
  if (corrected === null && !isExtreme) return null;

  return {
    direction: amount < min ? 'low' : 'high',
    entered: formatSalaryWithSymbol(amount, band.currency),
    suggested: corrected === null ? null : formatSalaryWithSymbol(corrected, band.currency),
  };
};
